import { endpoint } from '@shared/api/config';
import { useSessionStore } from '@shared/auth/session';

/**
 * The byte half of every upload on these surfaces. Two transports, and the split
 * is not a preference.
 *
 * **Artwork and documents go straight to storage.** The owning service issues a
 * presigned target and the browser PUTs to it, which is why that call is a bare
 * `fetch` rather than the enveloped client: the target is object storage, it
 * answers no `{success, message, data}`, and it must not carry the session's
 * access token. No credential is attached — the presigned URL *is* the
 * authorisation — the console never learns a bucket name or an object key, and
 * the upload is not the commit: the service commits by reading the object itself
 * at `:completeUpload`, so a failed PUT leaves a row the sweep collects rather
 * than a half-registered asset.
 *
 * **A source video goes through the assets service, over tus.** It is the one
 * upload on this console measured in gigabytes, and a single non-resumable
 * transfer of that size turns any dropped connection into a total loss. Resuming
 * needs a server that remembers the offset, and object storage remembers nothing
 * — so the bytes take the longer path deliberately, and a readable percentage
 * falls out of it as well.
 *
 * **Nothing about the protocol is a literal here.** The chunk size, the version
 * and the extensions arrive in `GET /uploadPolicy`; a size chosen on this side
 * would be refused by storage the moment the server's changed, halfway through a
 * multi-gigabyte upload.
 */

export class UploadTransferError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`The presigned upload target answered ${status}.`);
    this.name = 'UploadTransferError';
    this.status = status;
  }
}

/** A refusal from the resumable endpoint, carrying the service's own error code. */
export class ResumableUploadError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, code: string | null, message: string) {
    super(message);
    this.name = 'ResumableUploadError';
    this.status = status;
    this.code = code;
  }
}

/** The pixel dimensions of a chosen image, as the artwork registration declares them. */
export interface ImageGeometry {
  widthPx: number;
  heightPx: number;
}

/**
 * Read a chosen image's real dimensions before registering it.
 *
 * The artwork registration is refused without them — they are what the per-ratio
 * minimum is checked against, and a cover below it would be upscaled into every
 * card on the catalogue. They are read here rather than declared as a guess: the
 * service re-reads them out of the stored object at `:completeUpload` and refuses
 * a disagreement, so a guess would surface as a rejected upload after the whole
 * file had already been transferred.
 *
 * `createImageBitmap` decodes off the main thread and is the path every browser
 * this console supports takes. The `<img>` fallback exists because the decode is
 * the only step here with no synchronous alternative, and a runtime without the
 * bitmap API would otherwise be unable to upload a cover at all.
 */
export async function readImageGeometry(file: File): Promise<ImageGeometry> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    try {
      return { widthPx: bitmap.width, heightPx: bitmap.height };
    } finally {
      bitmap.close();
    }
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    return await new Promise<ImageGeometry>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve({ widthPx: image.naturalWidth, heightPx: image.naturalHeight });
      image.onerror = () => reject(new UploadTransferError(0));
      image.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function putToPresignedTarget(uploadTarget: string, file: File): Promise<void> {
  const response = await fetch(uploadTarget, {
    method: 'PUT',
    body: file,
    credentials: 'omit',
    headers: { 'Content-Type': file.type },
  });

  if (!response.ok) throw new UploadTransferError(response.status);
}

const TUS_VERSION = '1.0.0';

export interface ResumableUploadOptions {
  /** The registered asset the bytes belong to. Travels in `Upload-Metadata`. */
  mediaAssetId: string;
  /** Published by `GET /uploadPolicy`. Never chosen on this side. */
  chunkSizeBytes: number;
  /** Fractional progress, `0..1`, called during each chunk and after it. */
  onProgress: (fraction: number) => void;
  signal?: AbortSignal;
}

/**
 * Upload one video over tus, reporting progress as it goes.
 *
 * The loop is deliberately plain: create a session, then send `chunkSizeBytes` at
 * a time from the offset the **server** reports. The server's offset is the one
 * that counts — reading it back after every chunk is what makes a resumed upload
 * resume rather than restart, and it costs one header on a response already in
 * hand.
 *
 * `XMLHttpRequest` rather than `fetch` for the chunk itself, for one reason:
 * `fetch` cannot report upload progress. A percentage that moved once per 8 MiB
 * would sit still for minutes on a slow connection, which reads as a frozen
 * console rather than as a working upload.
 */
export async function uploadResumable(file: File, options: ResumableUploadOptions): Promise<void> {
  const sessionUrl = await createSession(file, options);

  try {
    let offset = 0;
    while (offset < file.size) {
      const end = Math.min(offset + options.chunkSizeBytes, file.size);
      const sentSoFar = offset;
      const stored = await sendChunk(sessionUrl, file.slice(offset, end), offset, {
        onProgress: (sentInChunk) => options.onProgress((sentSoFar + sentInChunk) / file.size),
        signal: options.signal,
      });
      // Trust the server's offset over the arithmetic: a chunk it already held is
      // answered with the offset it already had, and continuing from a local count
      // would write the next part over a gap.
      offset = stored;
      options.onProgress(offset / file.size);
    }
  } catch (error) {
    // **An abandoned session blocks its own asset.** The server holds one live
    // session per asset, so a session this console gave up on refuses every later
    // attempt on that asset until it expires — the operator retries the file that
    // just failed and is told the asset is busy, by itself. Termination is the
    // protocol's own answer and is best-effort: the refusal that got us here is
    // the one worth reporting, and a failed cleanup must not replace it.
    await terminateSession(sessionUrl).catch(() => undefined);
    throw error;
  }
}

async function createSession(file: File, options: ResumableUploadOptions): Promise<string> {
  const response = await fetch(endpoint('assets', '/uploads'), {
    method: 'POST',
    headers: {
      ...authorization(),
      'Tus-Resumable': TUS_VERSION,
      'Upload-Length': String(file.size),
      'Upload-Metadata': encodeMetadata({ mediaAssetId: options.mediaAssetId, filetype: file.type }),
    },
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (!response.ok) throw await refusal(response);

  const location = response.headers.get('Location');
  if (!location)
    throw new ResumableUploadError(response.status, null, 'The upload session carried no Location.');
  return sessionUrlFrom(location);
}

/**
 * Address the session through this console's own base, keeping only its id.
 *
 * **`Location` is not a URL this browser can use.** The service builds it from
 * the request it was reached at, and behind the ingress that is neither the
 * scheme the browser used — TLS is terminated at the edge, so the value names
 * `http` and the page refuses it as mixed content — nor the path it asked for,
 * since the service segment is stripped before proxying and never appears in the
 * header. The upload froze at 0 % with the PATCH blocked before it left the page.
 *
 * The id is the only part of that header this console did not already know, so it
 * is the only part kept. A relative `Location` is equally handled, and the day the
 * edge forwards its prefix the result is unchanged rather than doubled.
 */
function sessionUrlFrom(location: string): string {
  const sessionId = location.split('?')[0]?.replace(/\/+$/, '').split('/').pop() ?? '';
  if (!sessionId) throw new ResumableUploadError(0, null, 'The upload session carried no identifier.');
  return endpoint('assets', `/uploads/${sessionId}`);
}

/** Abandon a session, so the asset it holds is free for the next attempt. */
async function terminateSession(sessionUrl: string): Promise<void> {
  await fetch(sessionUrl, {
    method: 'DELETE',
    headers: { ...authorization(), 'Tus-Resumable': TUS_VERSION },
  });
}

function sendChunk(
  sessionUrl: string,
  chunk: Blob,
  offset: number,
  { onProgress, signal }: { onProgress: (sentInChunk: number) => void; signal?: AbortSignal | undefined },
): Promise<number> {
  return new Promise<number>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PATCH', sessionUrl, true);
    request.setRequestHeader('Tus-Resumable', TUS_VERSION);
    request.setRequestHeader('Upload-Offset', String(offset));
    request.setRequestHeader('Content-Type', 'application/offset+octet-stream');
    for (const [name, value] of Object.entries(authorization())) request.setRequestHeader(name, value);

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress(event.loaded);
    });

    request.addEventListener('load', () => {
      if (request.status < 200 || request.status >= 300) {
        reject(refusalFromXhr(request));
        return;
      }
      const stored = Number(request.getResponseHeader('Upload-Offset'));
      if (!Number.isFinite(stored)) {
        reject(
          new ResumableUploadError(request.status, null, 'The chunk response carried no Upload-Offset.'),
        );
        return;
      }
      resolve(stored);
    });

    request.addEventListener('error', () =>
      reject(new ResumableUploadError(0, null, 'The connection dropped during the upload.')),
    );
    request.addEventListener('abort', () =>
      reject(new ResumableUploadError(0, null, 'The upload was cancelled.')),
    );

    signal?.addEventListener('abort', () => request.abort(), { once: true });
    request.send(chunk);
  });
}

/** The in-memory access token, attached the way the enveloped client attaches it. */
function authorization(): Record<string, string> {
  const { accessToken } = useSessionStore.getState();
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

/**
 * `key base64(value)` pairs, comma-separated — the shape tus fixes for metadata.
 *
 * `btoa` takes Latin-1, so the value is UTF-8 encoded first. Without that a file
 * whose name or type carried a non-ASCII byte would throw here rather than upload.
 */
function encodeMetadata(pairs: Record<string, string>): string {
  return Object.entries(pairs)
    .map(([key, value]) => `${key} ${base64Utf8(value)}`)
    .join(',');
}

function base64Utf8(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

async function refusal(response: Response): Promise<ResumableUploadError> {
  const body = (await response.json().catch(() => null)) as { error_code?: string; message?: string } | null;
  return new ResumableUploadError(
    response.status,
    body?.error_code ?? null,
    body?.message ?? `The upload endpoint answered ${response.status}.`,
  );
}

function refusalFromXhr(request: XMLHttpRequest): ResumableUploadError {
  let code: string | null = null;
  let message = `The upload endpoint answered ${request.status}.`;
  try {
    const body = JSON.parse(request.responseText) as { error_code?: string; message?: string };
    code = body.error_code ?? null;
    message = body.message ?? message;
  } catch {
    // A refusal with no JSON body is still a refusal; the status carries it.
  }
  return new ResumableUploadError(request.status, code, message);
}
